import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import './ep.css';
import { epApi, showError } from './api';
import { addDays, deadlineText, DECISION_LABEL, md, programName, STAGES, todayIso } from './format';
import { useEpUser, useLoad } from './hooks';
import { ProgramCard, ProgramList, TrackedList, TrackedProgram, WeeksView } from './types';

type View = 'list' | 'board' | 'calendar';
const STAGE_CHIP: Record<string, string> = { researching: '', applying: 'a', submitted: 'ok', decision: 'ok' };
const stageLabel = (s: string) => STAGES.find(([k]) => k === s)?.[1] ?? s;

/** My Programs: everything the student tracks, what is waiting for her, and what is coming up. */
export default function MyProgramsPage() {
  const history = useHistory();
  const { userId } = useEpUser();
  const [view, setView] = useState<View>('list');
  const [calOpen, setCalOpen] = useState(false);

  const tracked = useLoad<TrackedList>(() => epApi.tracked(userId), [userId]);
  const weeks = useLoad<WeeksView>(() => epApi.weeks(userId), [userId]);
  const lists = useLoad<ProgramList[]>(() => epApi.lists(userId), [userId]);
  const saved = useLoad<ProgramCard[]>(() => epApi.saved(), []);

  const t = useMemo(() => tracked.data?.tracked ?? [], [tracked.data]);
  const limit = tracked.data?.plan_limit;
  const open = (pid: number) => history.push(`/summer-programs/programs?id=${pid}`);
  const trackedIds = new Set(t.map((x) => x.program_id));
  const savedOnly = (saved.data ?? []).filter((p) => !trackedIds.has(p.id));

  const comingUp = useMemo(() => {
    const today = todayIso();
    return t
      .flatMap((p) => p.items.filter((i) => i.date && i.date >= today && i.status !== 'done' && i.status !== 'not_applicable').map((i) => ({ ...i, program: p.name, program_id: p.program_id })))
      .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''))
      .slice(0, 6);
  }, [t]);

  const moveIt = async () => {
    const s = weeks.data?.suggestion;
    if (!s) return;
    try {
      await epApi.updateTask(userId, s.task_id, { student_target_date: s.to });
      toast.success(`Moved to ${md(s.to)}. Your calendar updates too.`);
      weeks.reload();
      tracked.reload();
    } catch (e) {
      showError(e);
    }
  };

  return (
    <div className="ep">
      <div className="mp">
        <div className="between" style={{ flexWrap: 'wrap' }}>
          <div>
            <h1>My programs</h1>
            <p className="body" style={{ marginTop: 4 }}>
              {limit?.free ? `Tracking ${limit.active} of ${limit.max} on the free plan` : `Tracking ${t.length} ${t.length === 1 ? 'program' : 'programs'}`}
            </p>
          </div>
          <div className="row">
            <div className="row" role="group" aria-label="View">
              {([['list', 'List'], ['board', 'Board by stage'], ['calendar', 'Calendar']] as [View, string][]).map(([k, l]) => (
                <button key={k} type="button" className={`chipb ${view === k ? 'on' : ''}`} aria-pressed={view === k} onClick={() => setView(k)}>
                  {l}
                </button>
              ))}
            </div>
            <button type="button" className="btn" onClick={() => history.push('/summer-programs')}>Find programs</button>
            <button type="button" className="btn" onClick={() => setCalOpen(true)}>Add to my calendar</button>
          </div>
        </div>

        {weeks.data && <ComingWeeks w={weeks.data} onMove={moveIt} />}

        {view === 'list' && (
          <div className="mpgrid">
            <div>
              <WaitingForYou lists={lists.data ?? []} onOpen={open} onAnswered={() => { lists.reload(); saved.reload(); }} />
              <section className="card" aria-label="Tracking">
                <h2>Tracking</h2>
                {tracked.loading && !t.length ? (
                  <p className="muted" style={{ marginTop: 12 }}>Loading</p>
                ) : t.length === 0 ? (
                  <p className="body" style={{ marginTop: 12 }}>
                    Nothing yet. Open a program and pick a plan to get a checklist with dates.
                  </p>
                ) : (
                  t.map((p) => <TrackedRow key={p.program_id} p={p} onOpen={() => open(p.program_id)} />)
                )}
              </section>
              {savedOnly.length > 0 && (
                <section className="card" aria-label="Saved">
                  <h2>Saved</h2>
                  <p className="muted small" style={{ marginTop: 4 }}>Pick a plan to get a checklist and reminders.</p>
                  {savedOnly.map((p) => (
                    <div key={p.id} className="trow">
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 500 }}>{programName(p)}</div>
                        <div className="muted small">{p.institution}</div>
                      </div>
                      <button type="button" className="btn sm" onClick={() => open(p.id)}>Pick a plan</button>
                    </div>
                  ))}
                </section>
              )}
            </div>
            <aside className="card" aria-label="Coming up">
              <h2>Coming up</h2>
              {comingUp.length === 0 ? (
                <p className="body small" style={{ marginTop: 10 }}>No dates yet. Dates appear when you pick a plan, or when a program posts its deadline.</p>
              ) : (
                <div style={{ marginTop: 8 }}>
                  {comingUp.map((i) => (
                    <div key={i.id} className="up">
                      <span className="d">{md(i.date)}</span>
                      <div>
                        <div style={{ fontWeight: 500 }}>{i.title}</div>
                        <button type="button" className="linkish small" style={{ color: 'var(--ep-muted)', whiteSpace: 'normal', textAlign: 'left' }} onClick={() => open(i.program_id)}>{i.program}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="muted small" style={{ marginTop: 12 }}>Reminders arrive 7 days and 1 day before each date.</p>
            </aside>
          </div>
        )}

        {view === 'board' && (
          <div className="board">
            {STAGES.map(([s, l]) => {
              const col = t.filter((p) => p.stage === s);
              return (
                <section key={s} className="col" aria-label={l}>
                  <div className="between"><h3>{l}</h3><span className="muted small">{col.length}</span></div>
                  {col.map((p) => (
                    <div key={p.program_id} className="bcard" role="button" tabIndex={0} onClick={() => open(p.program_id)} onKeyDown={(e) => e.key === 'Enter' && open(p.program_id)}>
                      <div style={{ fontWeight: 500 }}>{p.name}</div>
                      {p.next_step && <div className="body small" style={{ marginTop: 4 }}>Next: {p.next_step.title}{p.next_step.date ? ` · ${md(p.next_step.date)}` : ''}</div>}
                      <div className="muted small" style={{ marginTop: 4 }}>{`${p.done_count} of ${p.total_count} done`}{p.decision ? ` · ${DECISION_LABEL[p.decision]}` : ''}</div>
                    </div>
                  ))}
                  {col.length === 0 && <p className="muted small" style={{ marginTop: 10 }}>None</p>}
                </section>
              );
            })}
          </div>
        )}

        {view === 'calendar' && <MonthCalendar tracked={t} onOpen={open} />}
      </div>
      {calOpen && <CalendarLinkModal studentId={userId} onClose={() => setCalOpen(false)} />}
    </div>
  );
}

function ComingWeeks({ w, onMove }: { w: WeeksView; onMove: () => void }) {
  const s = w.suggestion;
  return (
    <section className="card" style={{ marginTop: 16 }} aria-label="Coming weeks">
      <div className="between" style={{ flexWrap: 'wrap' }}>
        <h2>Coming weeks</h2>
        <span className="muted small">Programs, colleges and your own tasks together</span>
      </div>
      <div className="weeks">
        {w.weeks.map((k) => (
          <div key={k.week_start} className={`wk ${k.heavy ? 'heavy' : ''}`} title={k.items.map((i) => `${md(i.date)} ${i.title}`).join('\n')}>
            <span className="lbl" style={{ marginBottom: 2 }}>{md(k.week_start)}{k.heavy ? ' · heavy week' : ''}</span>
            <div style={{ fontWeight: 500 }}>{`${k.count} ${k.count === 1 ? 'item' : 'items'}`}</div>
          </div>
        ))}
      </div>
      {s && (
        <div className="between" style={{ marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <p style={{ color: 'var(--gm-warning-ink, #b45309)' }}>
            The week of {md(s.week_start)} has {w.weeks.find((k) => k.week_start === s.week_start)?.count ?? 'several'} things due. Move "{s.title}" to {md(s.to)}?
          </p>
          <button type="button" className="btn sm" onClick={onMove}>Move it</button>
        </div>
      )}
    </section>
  );
}

function WaitingForYou({ lists, onOpen, onAnswered }: { lists: ProgramList[]; onOpen: (id: number) => void; onAnswered: () => void }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const suggestions = lists.filter((l) => l.is_suggestion).flatMap((l) => l.items.filter((i) => i.program && !i.in_student_programs && !i.student_response).map((i) => ({ ...i, from: l.name })));
  const shared = lists.filter((l) => !l.is_suggestion && l.created_by_role === 'parent' && l.items.length);
  if (!suggestions.length && !shared.length) return null;

  const answer = async (itemId: number, response: 'added' | 'not_for_me', programId: number) => {
    try {
      await epApi.respond(itemId, response);
      onAnswered();
      if (response === 'added') onOpen(programId);
      else toast.success('Got it. Your counselor will see your answer.');
    } catch (e) {
      showError(e);
    }
  };

  return (
    <section className="card" aria-label="Waiting for you" style={{ marginBottom: 16 }}>
      <h2>Waiting for you</h2>
      {suggestions.map((i) => (
        <div key={i.id} className="trow">
          <div style={{ minWidth: 0 }}>
            <span className="chip a">From your counselor</span>
            <div style={{ fontWeight: 500, marginTop: 8 }}>{programName(i.program!)}</div>
            {i.note && <p className="body small" style={{ marginTop: 2 }}>{i.note}</p>}
          </div>
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <button type="button" className="btn sm primary" onClick={() => answer(i.id, 'added', i.program!.id)}>Add and pick a plan</button>
            <button type="button" className="btn sm" onClick={() => answer(i.id, 'not_for_me', i.program!.id)}>Not for me</button>
          </div>
        </div>
      ))}
      {shared.map((l) => (
        <div key={l.id} className="trow" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <div className="between" style={{ alignItems: 'center' }}>
            <div style={{ minWidth: 0 }}>
              <span className="chip">A parent shared a list</span>
              <div style={{ fontWeight: 500, marginTop: 8 }}>{l.name}</div>
              <p className="body small" style={{ marginTop: 2 }}>{l.items.map((i) => (i.program ? programName(i.program) : '')).filter(Boolean).join(' · ')}</p>
            </div>
            <button type="button" className="btn sm" aria-expanded={expanded === l.id} onClick={() => setExpanded(expanded === l.id ? null : l.id)}>
              {expanded === l.id ? 'Close' : 'Open'}
            </button>
          </div>
          {expanded === l.id && (
            <div style={{ marginTop: 8 }}>
              {l.items.filter((i) => i.program).map((i) => (
                <div key={i.id} className="between" style={{ padding: '8px 0', alignItems: 'center' }}>
                  <span>{programName(i.program!)}{i.in_student_programs ? <span className="muted small"> · in my programs</span> : null}</span>
                  <button type="button" className="btn sm" onClick={() => onOpen(i.program!.id)}>View</button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

function TrackedRow({ p, onOpen }: { p: TrackedProgram; onOpen: () => void }) {
  const ns = p.next_step;
  return (
    <div className="trow">
      <div style={{ minWidth: 0 }}>
        <div className="row" style={{ gap: 8 }}>
          <span style={{ fontWeight: 500, fontSize: 15 }}>{p.name}</span>
          <span className={`chip ${STAGE_CHIP[p.stage]}`}>{p.decision ? DECISION_LABEL[p.decision] : stageLabel(p.stage)}</span>
        </div>
        {p.institution && <div className="muted small" style={{ fontWeight: 500, marginTop: 2 }}>{p.institution}</div>}
        {ns && (
          <div className="body small" style={{ marginTop: 6 }}>
            Next: {ns.title}
            {ns.date ? ` · ${ns.student_target_date ? 'your date' : 'deadline'} ${md(ns.date)}` : ''}
            {ns.created_by_role === 'counselor' && <span className="chip a" style={{ marginLeft: 6 }}>From your counselor</span>}
          </div>
        )}
        <div className="muted small" style={{ marginTop: 4 }}>
          {[deadlineText(p.deadline_kind, p.next_deadline, p.last_year_deadline_date), `${p.done_count} of ${p.total_count} done`, p.portal_url ? 'application link saved' : ''].filter(Boolean).join(' · ')}
        </div>
      </div>
      <button type="button" className="btn sm" onClick={onOpen}>Open</button>
    </div>
  );
}

function MonthCalendar({ tracked, onOpen }: { tracked: TrackedProgram[]; onOpen: (pid: number) => void }) {
  const [month, setMonth] = useState(() => todayIso().slice(0, 7));
  const first = `${month}-01`;
  const startDow = new Date(`${first}T00:00:00Z`).getUTCDay();
  const gridStart = addDays(first, -startDow);
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const events = tracked.flatMap((p) => p.items.filter((i) => i.date && i.status !== 'not_applicable').map((i) => ({ ...i, program: p.name, pid: p.program_id })));
  const shift = (n: number) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + n, 1));
    setMonth(d.toISOString().slice(0, 7));
  };
  const title = new Date(`${first}T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return (
    <section aria-label="Calendar" style={{ marginTop: 16 }}>
      <div className="between" style={{ alignItems: 'center' }}>
        <h2>{title}</h2>
        <div className="row">
          <button type="button" className="btn sm" onClick={() => shift(-1)} aria-label="Previous month">Previous</button>
          <button type="button" className="btn sm" onClick={() => setMonth(todayIso().slice(0, 7))}>Today</button>
          <button type="button" className="btn sm" onClick={() => shift(1)} aria-label="Next month">Next</button>
        </div>
      </div>
      <div className="cal">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d} className="h">{d}</div>)}
        {days.map((d) => (
          <div key={d} className={`c ${d.slice(0, 7) !== month ? 'out' : ''}`}>
            <span style={{ fontWeight: d === todayIso() ? 600 : 400 }}>{+d.slice(8)}</span>
            {events.filter((e) => e.date === d).map((e) => (
              <button key={e.id} type="button" className={`ev ${e.status === 'done' ? 'done' : ''}`} style={{ border: 0, width: '100%', textAlign: 'left', font: 'inherit' }} title={`${e.title} · ${e.program}`} onClick={() => onOpen(e.pid)}>
                {e.title}
              </button>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function CalendarLinkModal({ studentId, onClose }: { studentId: number; onClose: () => void }) {
  const link = useLoad<{ path: string }>(() => epApi.calendarLink(studentId), [studentId]);
  const base = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '');
  const url = link.data ? `${base}${link.data.path.startsWith('/') ? '' : '/'}${link.data.path}` : '';
  const webcal = url.replace(/^https?:/, 'webcal:');
  const copy = () => {
    navigator.clipboard?.writeText(url).then(() => toast.success('Link copied'), () => toast.error('Copy did not work. Select the link and copy it.'));
  };
  return (
    <div className="ep-modal-bg" role="dialog" aria-modal="true" aria-labelledby="ep-cal-title" onClick={onClose}>
      <div className="ep-modal ep" style={{ padding: 24, minHeight: 0 }} onClick={(e) => e.stopPropagation()}>
        <h2 id="ep-cal-title">Add your dates to your calendar</h2>
        <p className="body" style={{ marginTop: 8, lineHeight: 1.55 }}>
          Your program checklist, college deadlines and your own tasks show in Google, Apple or Outlook calendar and stay up to date. Calendar apps refresh every few hours.
        </p>
        {link.error ? (
          <p className="panel" style={{ marginTop: 12 }}>The calendar link did not load. Please try again.</p>
        ) : (
          <>
            <input className="in" readOnly value={url || 'Loading'} aria-label="Calendar link" style={{ marginTop: 14 }} onFocus={(e) => e.target.select()} />
            <ol className="body small" style={{ marginTop: 12, paddingLeft: 18, lineHeight: 1.7 }}>
              <li>Google Calendar: Other calendars, then From URL, and paste the link.</li>
              <li>Apple Calendar: tap Subscribe below, or File, then New Calendar Subscription.</li>
              <li>Outlook: Add calendar, then Subscribe from web, and paste the link.</li>
            </ol>
          </>
        )}
        <div className="foot">
          <div className="row">
            <button type="button" className="btn primary" disabled={!url} onClick={copy}>Copy link</button>
            {url && <a className="btn" href={webcal}>Subscribe</a>}
          </div>
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
